import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { useUploadCustomFieldProof } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useUploadCustomFieldProof"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type DocumentStateUpdater = {
  setValue: Dispatch<SetStateAction<CustomFieldValue>>
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
}

export const useCustomFieldProofUpload = ({
  customFieldValueId,
  operationId,
  stateUpdaters,
  inputOpen,
}: {
  customFieldValueId: string
  operationId: number
  stateUpdaters: DocumentStateUpdater
  inputOpen: () => void
}) => {
  const { t } = useTranslation("traceability")
  const { setValue, setCustomFieldValues } = stateUpdaters

  const { mutate: uploadCustomFieldProof, isPending: isUploading } =
    useUploadCustomFieldProof({
      onSuccess: async (data, variables) => {
        const fileName = variables.file.name ?? null

        setValue((prev) => {
          return {
            ...prev,
            selectedFile: null,
            document: {
              id: data,
              originalName: fileName,
            },
          }
        })
        setCustomFieldValues((prev) => {
          return prev.map((field) => {
            if (field.id === customFieldValueId) {
              const updated = {
                ...field,
                document: {
                  id: data,
                  originalName: fileName,
                },
              }
              return updated
            }
            return field
          })
        })

        inputOpen()
        showSuccessNotification({
          message: t("customFields.updateCustomField.customFieldProof.message"),
          title: t("customFields.updateCustomField.customFieldProof.title"),
        })
        await queryClient.invalidateQueries({
          queryKey: ["listTraceabilityRequests", operationId],
          refetchType: "none",
        })
      },
      onError: async (error) => {
        setValue((prev) => ({
          ...prev,
          selectedFile: null,
        }))
        showAxiosError({
          showNotification: true,
          error,
        })
        await queryClient.invalidateQueries({
          queryKey: ["listTraceabilityRequests", operationId],
        })
      },
    })

  return {
    uploadCustomFieldProof,
    isUploading,
  }
}
